import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useUserRoles } from "@/hooks/useUserRoles";

export type NotificationType = "profile" | "connection" | "help_request" | "ask_nedra";

export interface AdminNotification {
  id: string;
  type: NotificationType;
  title: string;
  description: string;
  createdAt: Date;
}

const SNOOZE_STORAGE_KEY = "admin_snoozed_notifications";
const SNOOZE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

interface SnoozeEntry {
  type: NotificationType;
  itemId: string;
  expiresAt: number;
}

function getSnoozedItems(): SnoozeEntry[] {
  try {
    const stored = localStorage.getItem(SNOOZE_STORAGE_KEY);
    if (!stored) return [];
    const items = JSON.parse(stored) as SnoozeEntry[];
    // Filter out expired entries
    const now = Date.now();
    return items.filter(item => item.expiresAt > now);
  } catch {
    return [];
  }
}

function setSnoozedItems(items: SnoozeEntry[]) {
  localStorage.setItem(SNOOZE_STORAGE_KEY, JSON.stringify(items));
}

function isSnoozed(type: NotificationType, itemId: string): boolean {
  const items = getSnoozedItems();
  return items.some(item => item.type === type && item.itemId === itemId);
}

function addSnooze(type: NotificationType, itemId: string) {
  const items = getSnoozedItems();
  const filtered = items.filter(item => !(item.type === type && item.itemId === itemId));
  filtered.push({
    type,
    itemId,
    expiresAt: Date.now() + SNOOZE_DURATION_MS,
  });
  setSnoozedItems(filtered);
}

export function useAdminNotifications() {
  const { user } = useAuth();
  const { data: roles } = useUserRoles();
  const isAdmin = roles?.includes("admin") ?? false;
  
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [currentNotification, setCurrentNotification] = useState<AdminNotification | null>(null);

  // Add a notification to the queue
  const addNotification = useCallback((notification: AdminNotification) => {
    if (isSnoozed(notification.type, notification.id)) {
      return;
    }
    
    setNotifications(prev => {
      // Don't add duplicates
      if (prev.some(n => n.id === notification.id && n.type === notification.type)) {
        return prev;
      }
      return [...prev, notification];
    });
  }, []);

  // Show next notification in queue
  useEffect(() => {
    if (!currentNotification && notifications.length > 0) {
      setCurrentNotification(notifications[0]);
      setNotifications(prev => prev.slice(1));
    }
  }, [currentNotification, notifications]);

  // Check for expired snoozes periodically
  useEffect(() => {
    const interval = setInterval(() => {
      // Clean up expired snoozes
      const items = getSnoozedItems();
      setSnoozedItems(items);
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Subscribe to realtime changes
  useEffect(() => {
    if (!user?.id || !isAdmin) return;

    // Subscribe to profiles changes (new unpublished profiles)
    const profileChannel = supabase
      .channel("admin-profile-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "profiles",
          filter: "is_published=eq.false",
        },
        (payload) => {
          const profile = payload.new as any;
          
          // Skip notification for bulk-imported users (they have imported_at set)
          // Admins can see these in the Pending Profiles tab with "Imported" filter
          if (profile.imported_at) {
            return;
          }
          
          addNotification({
            id: profile.id,
            type: "profile",
            title: "New Profile Pending Review",
            description: `${profile.full_name || "New user"} (${profile.role || "unknown role"})`,
            createdAt: new Date(profile.created_at),
          });
        }
      )
      .subscribe();

    // Subscribe to connections changes (mentorship type with pending status)
    const connectionChannel = supabase
      .channel("admin-connection-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "connections",
        },
        (payload) => {
          const connection = payload.new as any;
          // Only notify for pending mentorship connections
          if (connection.status === "pending" && connection.connection_type === "mentorship") {
            addNotification({
              id: connection.id,
              type: "connection",
              title: "New Connection Request",
              description: "A new mentorship connection needs your approval",
              createdAt: new Date(connection.created_at),
            });
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "connections",
        },
        (payload) => {
          const connection = payload.new as any;
          const oldConnection = payload.old as any;
          // Only notify if status changed TO pending for mentorship connections
          if (
            connection.status === "pending" && 
            connection.connection_type === "mentorship" &&
            oldConnection?.status !== "pending"
          ) {
            addNotification({
              id: connection.id,
              type: "connection",
              title: "Connection Request Needs Review",
              description: "A mentorship connection requires your attention",
              createdAt: new Date(),
            });
          }
        }
      )
      .subscribe();

    // Subscribe to help_requests changes
    const helpChannel = supabase
      .channel("admin-help-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "help_requests",
          filter: "status=eq.pending",
        },
        (payload) => {
          const request = payload.new as any;
          addNotification({
            id: request.id,
            type: "help_request",
            title: "New Help Request",
            description: request.title || "A user needs assistance",
            createdAt: new Date(request.created_at),
          });
        }
      )
      .subscribe();

    // Subscribe to support_threads changes (Ask Nedra)
    const askNedraChannel = supabase
      .channel("admin-ask-nedra-notifications")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "support_threads",
        },
        async (payload) => {
          const thread = payload.new as any;
          // Fetch user name for better context
          const { data: profile } = await supabase
            .from("profiles")
            .select("full_name")
            .eq("id", thread.user_id)
            .single();
          
          addNotification({
            id: thread.id,
            type: "ask_nedra",
            title: "New Ask Nedra Request",
            description: `${profile?.full_name || "A user"} submitted a support request`,
            createdAt: new Date(thread.created_at),
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(profileChannel);
      supabase.removeChannel(connectionChannel);
      supabase.removeChannel(helpChannel);
      supabase.removeChannel(askNedraChannel);
    };
  }, [user?.id, isAdmin, addNotification]);

  const dismissNotification = useCallback(() => {
    setCurrentNotification(null);
  }, []);

  const snoozeNotification = useCallback(() => {
    if (currentNotification) {
      addSnooze(currentNotification.type, currentNotification.id);
      setCurrentNotification(null);
    }
  }, [currentNotification]);

  return {
    currentNotification,
    dismissNotification,
    snoozeNotification,
    isAdmin,
  };
}
